import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import { imageUrl } from "@/lib/api";
import { classifyImageFit, computeHeroContainerClass, getFocalObjectPosition, hasMobileOverride } from "@/lib/bannerRenderer";

const AUTOPLAY_MS = 5000;
const MAX_SLIDES = 5;

const btnRadius = (style) => (style === "pill" ? "9999px" : style === "sharp" ? "2px" : "12px");

const ALIGN = {
  center: "items-center text-center left-0 right-0 mx-auto",
  right: "items-end text-right right-0",
  left: "items-start text-left left-0",
};

function Slide({ b, radius, eager }) {
  const overlay = Math.min(Math.max(Number(b.overlay_strength ?? 60), 0), 100) / 100;
  const align = ALIGN[b.text_alignment] || ALIGN.left;
  const overlayDirection = b.text_alignment === "right" ? "270deg" : b.text_alignment === "center" ? "0deg" : "90deg";
  const cta = b.button_text || "Belanja Sekarang";
  const link = b.button_link || b.link_url || "/katalog";
  // Focal point (default center) menentukan sisi gambar yang tetap terlihat
  // saat controlled crop object-cover — dipakai baik oleh primary banner
  // (fallback responsive di mobile) maupun mobile override.
  const focalCls = getFocalObjectPosition(b.focal_point);
  return (
    <div className="storefront-hero-slide relative w-full h-full shrink-0" data-testid="hero-slide">
      {b.image_url && (
        <>
          {hasMobileOverride(b) ? (
            <>
              <img src={imageUrl(b.mobile_image_url)} alt={b.title || "Banner promosi"} loading={eager ? "eager" : "lazy"}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className={`md:hidden absolute inset-0 w-full h-full object-cover ${focalCls}`} />
              <img src={imageUrl(b.image_url)} alt={b.title || "Banner promosi"} loading={eager ? "eager" : "lazy"}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className={`hidden md:block absolute inset-0 w-full h-full object-cover ${focalCls}`} />
            </>
          ) : (
            <img src={imageUrl(b.image_url)} alt={b.title || "Banner promosi"} loading={eager ? "eager" : "lazy"}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              className={`absolute inset-0 w-full h-full object-cover ${focalCls}`} />
          )}
        </>
      )}
      <div className="absolute inset-0"
        style={{ background: `linear-gradient(${overlayDirection}, rgba(0,0,0,${(overlay * 0.88).toFixed(2)}) 0%, rgba(0,0,0,${(overlay * 0.48).toFixed(2)}) 48%, rgba(0,0,0,${(overlay * 0.16).toFixed(2)}) 100%)` }} />
      <div className={`storefront-hero-copy absolute inset-y-0 px-5 py-7 sm:p-10 lg:p-14 text-white max-w-2xl flex flex-col justify-center ${align}`}>
        {b.badge && (
          <div className="storefront-hero-badge brand-bg-primary" data-testid="hero-slide-badge">
            {b.badge}
          </div>
        )}
        <h2 className="font-display text-[30px] sm:text-[42px] lg:text-[52px] font-extrabold tracking-[-0.035em] leading-[1.04] mb-3 drop-shadow-sm">{b.title}</h2>
        {b.subtitle && <p className="max-w-xl text-sm sm:text-base lg:text-[17px] leading-relaxed text-white/88 mb-5 sm:mb-6">{b.subtitle}</p>}
        <Link to={link}
          className="storefront-hero-cta inline-flex w-fit items-center gap-2 bg-white text-[#1A1A1A] font-bold px-5 py-3 hover:text-white transition-colors hover:[background:var(--brand-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
          style={{ borderRadius: radius }} data-testid="hero-slide-cta">
          {cta} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}

/* Global Commerce Premium — slide fokus gambar: TANPA overlay/copy/CTA.
   Seluruh slide clickable bila banner punya tujuan (link_url/button_link).
   Data banner existing tidak berubah; CTA legacy tetap dipakai oleh Slide. */
/* GCP image-focus — Marketplace Landscape Mode: satu upload, kontainer landscape,
   object-cover + focal_point untuk semua sumber landscape. Fallback darurat HANYA
   untuk sumber potret (isPortraitSource): object-contain + backdrop blur dari URL yang
   sama (cache browser). Dihitung dari naturalWidth/Height saat load. */
function GcpImage({ src, alt, eager, focalCls, visibility = "" }) {
  const [fit, setFit] = useState("cover");
  const [failed, setFailed] = useState(false);
  const onLoad = (e) => {
    const img = e.currentTarget;
    setFit(classifyImageFit({ naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight }));
  };
  if (failed) return null;
  const contain = fit === "contain";
  return (
    <>
      {contain && (
        <>
          <img src={src} alt="" aria-hidden="true" loading={eager ? "eager" : "lazy"} decoding="async"
            className={`${visibility} gcp-hero-backdrop absolute inset-0 w-full h-full object-cover`} />
          <div aria-hidden="true" className={`${visibility} gcp-hero-backdrop-dim absolute inset-0`} />
        </>
      )}
      <img src={src} alt={alt} loading={eager ? "eager" : "lazy"} decoding="async"
        fetchPriority={eager ? "high" : "auto"} onLoad={onLoad} onError={() => setFailed(true)}
        data-fit={fit}
        className={`${visibility} absolute inset-0 w-full h-full ${contain ? "object-contain" : "object-cover"} ${focalCls}`} />
    </>
  );
}

function ImageSlide({ b, eager }) {
  const focalCls = getFocalObjectPosition(b.focal_point);
  const alt = b.title || "Banner promosi";
  const link = (b.link_url || b.button_link || "").trim();
  const external = /^(https?:)?\/\//i.test(link);
  const imgs = b.image_url ? (
    hasMobileOverride(b) ? (
      <>
        <GcpImage src={imageUrl(b.mobile_image_url)} alt={alt} eager={eager} focalCls={focalCls} visibility="md:hidden" />
        <GcpImage src={imageUrl(b.image_url)} alt={alt} eager={eager} focalCls={focalCls} visibility="hidden md:block" />
      </>
    ) : (
      <GcpImage src={imageUrl(b.image_url)} alt={alt} eager={eager} focalCls={focalCls} />
    )
  ) : null;
  const cls = "storefront-hero-slide gcp-hero-slide relative block w-full h-full shrink-0 overflow-hidden focus-visible:outline-none";
  if (!link) return <div className={cls} data-testid="hero-slide" data-variant="image-focus">{imgs}</div>;
  if (external) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" className={cls} aria-label={alt}
        data-testid="hero-slide" data-variant="image-focus">{imgs}</a>
    );
  }
  return (
    <Link to={link} className={cls} aria-label={alt} data-testid="hero-slide" data-variant="image-focus">{imgs}</Link>
  );
}

function FallbackHero({ branding, radius, heroCls }) {
  return (
    <section
      className={`${heroCls} storefront-hero-fallback grid place-items-center p-6 sm:p-10`}
      style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--brand-primary) 18%, var(--brand-background)), color-mix(in srgb, var(--brand-accent) 12%, var(--brand-background)))" }}
      data-testid="hero-fallback"
    >
      <div className="text-center max-w-xl">
        <h1 className="font-display text-[32px] sm:text-5xl font-extrabold tracking-[-0.035em] text-[color:var(--brand-text)]">{branding.general.name}</h1>
        <p className="mt-3 text-sm sm:text-base leading-relaxed text-[color:var(--brand-text)] opacity-70">
          {branding.general.description || branding.general.tagline || "Belanja mudah, langsung dari toko kami"}
        </p>
        <Link to="/katalog"
          className="mt-6 inline-flex min-h-11 items-center gap-2 text-white font-bold px-6 py-3 transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-primary)] focus-visible:ring-offset-2"
          style={{ backgroundColor: "var(--brand-primary)", borderRadius: radius }}
          data-testid="hero-fallback-cta">
          Belanja Sekarang <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

export const HeroBannerSlider = ({ banners, layout = "banner-sections-grid", variant = "default" }) => {
  const imageFocus = variant === "image-focus";
  const { branding } = useBranding();
  const radius = btnRadius(branding.theme.button_style);
  const slides = (banners || [])
    .filter((b) => b.is_active !== false && b.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .slice(0, MAX_SLIDES);
  const count = slides.length;
  // MOBILE BANNER FIX (900×1200 3:4) — lihat heroContainer.js untuk aturan &
  // rasional. Ringkas: aspect-[3/4] di mobile ketika semua slide punya
  // mobile_image_url, kembali ke landscape heights di md+.
  const heroCls = computeHeroContainerClass({ slides, layout });

  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [hidden, setHidden] = useState(false);
  const touchX = useRef(null);

  const go = useCallback((n) => {
    if (count < 1) return;
    setIdx(((n % count) + count) % count);
  }, [count]);

  useEffect(() => {
    if (count < 2 || hovered || hidden) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [count, hovered, hidden]);

  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  if (count === 0) return <FallbackHero branding={branding} radius={radius} heroCls={heroCls} />;

  return (
    <section
      className={`${heroCls} storefront-hero group outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-primary)] focus-visible:ring-offset-2`}
      data-testid="hero-banner-slider"
      data-variant={variant}
      data-slide-count={count}
      data-active-slide={idx}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "ArrowLeft") go(idx - 1); if (e.key === "ArrowRight") go(idx + 1); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) (dx > 0 ? go(idx - 1) : go(idx + 1));
        touchX.current = null;
      }}
    >
      <div className="flex h-full transition-transform duration-700 ease-out motion-reduce:transition-none"
        style={{ transform: `translateX(-${idx * 100}%)` }}>
        {slides.map((b, i) => (imageFocus
          ? <ImageSlide key={b.id || i} b={b} eager={i === 0} />
          : <Slide key={b.id || i} b={b} radius={radius} eager={i === 0} />))}
      </div>

      {count > 1 && (
        <>
          <button aria-label="Sebelumnya" onClick={() => go(idx - 1)}
            className="storefront-hero-arrow hidden md:grid left-4"
            data-testid="hero-prev-btn">
            <ChevronLeft size={20} />
          </button>
          <button aria-label="Berikutnya" onClick={() => go(idx + 1)}
            className="storefront-hero-arrow hidden md:grid right-4"
            data-testid="hero-next-btn">
            <ChevronRight size={20} />
          </button>
          <div className="storefront-hero-dots absolute bottom-3.5 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid="hero-dots">
            {slides.map((_, i) => (
              <button key={i} aria-label={`Slide ${i + 1}`} onClick={() => go(i)}
                className="h-2 rounded-full transition-all duration-300 motion-reduce:transition-none"
                style={{ width: i === idx ? 24 : 8, backgroundColor: i === idx ? "#FFFFFF" : "rgba(255,255,255,0.48)" }}
                data-testid={`hero-dot-${i}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
